import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import React, { useCallback, useEffect, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import {
  boldIcon,
  bulletPointIcon,
  centerAlignIcon,
  colorPickerIcon,
  italicIcon,
  justifyAlignIcon,
  leftAlignIcon,
  numberedLineIcon,
  rightAlignIcon,
  underlineIcon,
} from "./icons";
import styles from "./text-editor.module.scss";

export interface TextEditorProps {
  htmlText: string;
  showListOption?: boolean;
  onTextEditorBlur?: (htmlText: string) => void;
  onSubmit?: (htmlText: string) => void;
}

export const TextEditor = ({
  htmlText,
  showListOption = true,
  onTextEditorBlur = () => undefined,
}: TextEditorProps) => {
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());

  useEffect(() => {
    if (htmlText.length > 0) {
      const draftJsObj = htmlToDraft(htmlText);
      const { contentBlocks, entityMap } = draftJsObj;
      setEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(contentBlocks, entityMap)));
    } else setEditorState(EditorState.createEmpty());
  }, [htmlText]);

  const onBlurEditor = useCallback(() => {
    const text = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    const textWithoutNewLines = text.replace(/(\r\n|\n|\r)/gm, "");
    onTextEditorBlur(textWithoutNewLines);
  }, [editorState, onTextEditorBlur]);

  return (
    <Editor
      placeholder={"Enter description..."}
      editorState={editorState}
      onEditorStateChange={setEditorState}
      onBlur={onBlurEditor}
      editorClassName={styles.textEditor}
      toolbar={{
        options: ["inline", "colorPicker", "textAlign", "list"],
        inline: {
          options: ["bold", "italic", "underline"],
          bold: { icon: boldIcon, className: styles.editorCustomImage },
          italic: { icon: italicIcon, className: styles.editorCustomImage },
          underline: { icon: underlineIcon, className: styles.editorCustomImage },
        },
        colorPicker: { icon: colorPickerIcon, className: styles.editorCustomImage },
        textAlign: {
          left: { icon: leftAlignIcon, className: styles.editorCustomImage },
          center: { icon: centerAlignIcon, className: styles.editorCustomImage },
          right: { icon: rightAlignIcon, className: styles.editorCustomImage },
          justify: { icon: justifyAlignIcon, className: styles.editorCustomImage },
        },
        list: {
          options: showListOption ? ["unordered", "ordered"] : [],
          unordered: { icon: bulletPointIcon, className: styles.editorCustomImage },
          ordered: { icon: numberedLineIcon, className: styles.editorCustomImage },
        },
      }}
    />
  );
};
